import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Camera, Megaphone, Users, ShieldCheck, FileSignature, Sparkles,
  Check, X, Plus, ChevronRight, Star, PhoneCall, Compass, Rocket, Timer,
  Landmark, Lock, Award, BadgeCheck,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatToExpect from "../components/WhatToExpect";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider, useAppModals } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { LANDLORD_SERVICES, LANDLORD_COMPARISON, LANDLORD_INCLUDED, LANDLORD_PROCESS, LANDLORD_FAQ, MARKETING_COMPARE, TESTIMONIAL_BG, CONTACT } from "../mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

const ICONS = { Camera, Megaphone, Users, ShieldCheck, FileSignature, Sparkles };
const PROCESS_ICONS = { Compass, Rocket, ShieldCheck };

function Cell({ v }) {
  if (v === "yes")
    return (
      <span className="grid h-6 w-6 place-items-center rounded-full bg-[#35a4a4] text-white">
        <Check size={14} strokeWidth={3} />
      </span>
    );
  if (v === "addon")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#eaa32a]/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#b9822a]">
        <Plus size={11} strokeWidth={3} /> Add-on
      </span>
    );
  return <X size={16} className="text-gray-300" />;
}

function Content() {
  const navigate = useNavigate();
  const { openValuation, openEnquiry } = useAppModals();
  const [activeExample, setActiveExample] = useState(0);

  React.useEffect(() => {
    const id = "faq-jsonld";
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: LANDLORD_FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    let s = document.getElementById(id);
    if (!s) {
      s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(data);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);

  return (
    <div className="App">
      <Seo title="Landlords | New Space Lettings & Management, Hackney" description="First-class lettings without the premium price. See what New Space offers landlords across Let Only, Rent Collection and Full Management, with fees tailored to your portfolio." path="/landlords" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28">
        <img src={TESTIMONIAL_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#16323f]/85" />
        <div className="relative z-10 mx-auto max-w-[1000px] px-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">For Landlords</p>
          <h1 className="mt-4 font-display text-4xl font-700 uppercase leading-tight tracking-wide text-white lg:text-6xl">
            First-class lettings, without the premium price
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-white/85">
            An independent, boutique lettings and management specialist rooted in Hackney — with London-wide
            reach through our partnership with HAUS Block Management. Excellent value, reliable management
            and a single, senior point of contact.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button onClick={openValuation}
              className="group flex items-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Request a free valuation
              <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* Service tiers */}
      <section className="bg-[#faf7f0] py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              Three ways to work with us
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <p className="mx-auto mt-4 max-w-2xl text-[15px] text-[#5b5450]">
              Choose the level of service that suits you. Our fees are tailored to your needs and the
              size of your portfolio &mdash; get in touch for a bespoke, no-obligation quote.
            </p>
          </div>
          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            {LANDLORD_SERVICES.map((s) => (
              <div key={s.name}
                className={`relative flex flex-col rounded-[1.75rem] border p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                  s.highlight ? "border-[#ec008c] bg-white ring-2 ring-[#ec008c]/20" : "border-[#e5ddd0] bg-white"
                }`}>
                {s.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#ec008c] px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-2xl font-600 text-[#16323f]">{s.name}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#7a716a]">{s.tagline}</p>
                <span className="mt-4 block h-[2px] w-12 rounded-full bg-[#eaa32a]" />
                <ul className="mt-6 flex-1 space-y-3">
                  {s.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#5b5450]">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#35a4a4] text-white">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
                <button onClick={() => openEnquiry(s.name)}
                  className={`mt-8 flex items-center justify-center gap-2 rounded-full py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                    s.highlight ? "bg-[#ec008c] text-white hover:bg-[#c60074]" : "border border-[#16323f]/25 text-[#16323f] hover:border-[#ec008c] hover:text-[#ec008c]"
                  }`}>
                  Enquire about {s.name}
                  <ChevronRight size={15} />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-[#7a716a]">
            In line with the Tenant Fees Act 2019, we never charge tenants a letting, renewal or admin fee.
          </p>
        </div>
      </section>

      {/* Marketing that works — before/after */}
      <section className="bg-[#f3ede3] py-16 lg:py-24" data-testid="marketing-compare-section">
        <div className="mx-auto max-w-[1100px] px-5">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">For Landlords &middot; Marketing That Works</p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              Presentation sells the property
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <div className="mx-auto mt-6 max-w-3xl space-y-4 text-[15px] leading-relaxed text-[#5b5450]">
              <p>
                A tenanted flat is never photographed empty. Drying racks, moving boxes and everyday clutter
                are the reality of most instructions &mdash; and the biggest reason a listing gets scrolled past.
              </p>
              <p>
                Every property we market gets the same treatment before it goes live: professional photography,
                then digital decluttering and enhancement, so it&rsquo;s the room a tenant pictures living in.
              </p>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-[900px]">
            <div className="mb-5 flex justify-center gap-3" data-testid="marketing-tabs">
              {MARKETING_COMPARE.examples.map((ex, i) => (
                <button
                  key={ex.id}
                  onClick={() => setActiveExample(i)}
                  data-testid={`marketing-tab-${ex.id}`}
                  className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                    activeExample === i
                      ? "bg-[#16323f] text-white"
                      : "border border-[#16323f]/20 bg-white text-[#16323f] hover:border-[#ec008c] hover:text-[#ec008c]"
                  }`}
                >
                  {ex.label}
                </button>
              ))}
            </div>
            <BeforeAfterSlider
              key={MARKETING_COMPARE.examples[activeExample].id}
              before={MARKETING_COMPARE.examples[activeExample].before}
              after={MARKETING_COMPARE.examples[activeExample].after}
              beforeAlt={`Original ${MARKETING_COMPARE.examples[activeExample].label.toLowerCase()} before enhancement`}
              afterAlt={`Same ${MARKETING_COMPARE.examples[activeExample].label.toLowerCase()} after professional decluttering and enhancement`}
            />
            <p className="mt-4 text-center text-xs italic text-[#7a716a]">
              Same room, same angle &mdash; illustrative example, decluttering and styling only, no structural or size changes.
            </p>
          </div>

          <div className="mt-14 grid gap-8 sm:grid-cols-3">
            {MARKETING_COMPARE.columns.map((c, i) => {
              const Icon = [Camera, Timer, ShieldCheck][i];
              return (
                <div key={c.title} className="text-center sm:text-left">
                  <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-[#16323f] text-[#d8b074] sm:mx-0">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-600 uppercase tracking-wide text-[#16323f]">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5b5450]">{c.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button onClick={openValuation} data-testid="marketing-valuation-btn"
              className="group flex items-center justify-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Request a Valuation <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* Detailed comparison */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1100px] px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              What&rsquo;s included in each service
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <p className="mx-auto mt-4 max-w-2xl text-[15px] text-[#5b5450]">
              A clear breakdown of what you get with each level of service.
            </p>
          </div>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b-2 border-[#e5ddd0]">
                  <th className="py-4 pr-4 font-display text-sm font-500 uppercase tracking-wider text-[#7a716a]">Service</th>
                  <th className="px-3 py-4 text-center">
                    <span className="font-display text-base font-600 text-[#16323f]">Full Management</span>
                    <span className="mt-1 block text-[10px] font-semibold uppercase tracking-widest text-[#ec008c]">Most popular</span>
                  </th>
                  <th className="px-3 py-4 text-center font-display text-base font-600 text-[#16323f]">Rent Collection</th>
                  <th className="px-3 py-4 text-center font-display text-base font-600 text-[#16323f]">Let Only</th>
                </tr>
              </thead>
              <tbody>
                {LANDLORD_COMPARISON.map((g) => (
                  <React.Fragment key={g.group}>
                    <tr className="bg-[#f3ede3]">
                      <td colSpan={4} className="px-3 py-2 font-display text-xs font-600 uppercase tracking-widest text-[#c9a25e]">
                        {g.group}
                      </td>
                    </tr>
                    {g.rows.map((r) => (
                      <tr key={r.label} className="border-b border-[#eee7db]">
                        <td className="py-3 pr-4 text-sm text-[#5b5450]">{r.label}</td>
                        <td className="px-3 py-3"><div className="flex justify-center"><Cell v={r.full} /></div></td>
                        <td className="px-3 py-3"><div className="flex justify-center"><Cell v={r.rent} /></div></td>
                        <td className="px-3 py-3"><div className="flex justify-center"><Cell v={r.let} /></div></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-center text-xs text-[#7a716a]">
            &ldquo;Add-on&rdquo; items are available on our Let Only service for a small additional charge. Fees for every
            service are tailored to your needs and portfolio &mdash; ask us for a bespoke quote.
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="bg-[#f3ede3] py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              Included with every service
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
          </div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {LANDLORD_INCLUDED.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <div key={f.title} className="rounded-2xl border border-[#e5ddd0] bg-[#faf7f0] p-7">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-[#16323f] text-[#d8b074]">
                    <Icon size={24} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-500 text-[#16323f]">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5b5450]">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="bg-[#e8e5dd] py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#1c1c20] lg:text-4xl">
              {LANDLORD_PROCESS.title}
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-[#5b5450]">
              {LANDLORD_PROCESS.intro}
            </p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {LANDLORD_PROCESS.steps.map((s) => {
              const Icon = PROCESS_ICONS[s.icon];
              return (
                <div
                  key={s.n}
                  className="relative flex flex-col rounded-[1.75rem] bg-[#16323f] p-8 pb-16 shadow-lg transition-transform duration-300 hover:-translate-y-1.5"
                >
                  <span className="grid h-16 w-16 place-items-center rounded-2xl bg-[#d8b074]/15 text-[#d8b074]">
                    <Icon size={30} strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-500 text-[#d8b074]">{s.title}</h3>
                  <p className="mt-4 flex-1 text-[15px] leading-relaxed text-gray-300">{s.text}</p>
                  <span className="absolute -bottom-3 right-6 grid h-16 w-16 place-items-center rounded-full bg-[#0e2530] font-display text-2xl font-700 text-[#d8b074] ring-8 ring-[#e8e5dd]">
                    {s.n}
                  </span>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* What You Can Expect */}
      <WhatToExpect />

      {/* Why landlords stay with us */}
      <section className="bg-[#e8e5dd] pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1100px] px-5">
          <div className="rounded-2xl bg-[#16323f] p-8 text-center lg:p-12">
            <h3 className="font-display text-2xl font-600 uppercase tracking-wide text-[#d8b074] lg:text-3xl">
              {LANDLORD_PROCESS.closingTitle}
            </h3>
            <p className="mx-auto mt-4 max-w-3xl text-[15px] leading-relaxed text-white/85">
              {LANDLORD_PROCESS.closingText}
            </p>
          </div>
        </div>
      </section>

      {/* You're in safe hands — trust & protection */}
      <section className="relative overflow-hidden bg-[#0e2530] py-16 lg:py-24" data-testid="safe-hands-section">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#35a4a4]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#d8b074]/10 blur-3xl" />
        <div className="relative mx-auto max-w-[1100px] px-5">
          <div className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#d8b074]/30 bg-[#d8b074]/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#d8b074]">
              <ShieldCheck size={14} /> Regulated &amp; Protected
            </p>
            <h2 className="mt-5 font-display text-3xl font-600 uppercase tracking-wide text-white lg:text-4xl">
              You&rsquo;re in safe hands
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-white/80">
              Your property and your money are protected at every step. We&rsquo;re fully covered by the
              industry&rsquo;s leading redress, client-money and deposit-protection schemes &mdash; so you can
              let with complete peace of mind.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Award,
                title: "The Property Ombudsman",
                tag: "Redress Scheme",
                text: "We're a member of TPO, giving landlords and tenants access to free, independent dispute resolution and holding us to a strict Code of Practice.",
                badge: "Membership No. D04002",
                testid: "safe-hands-tpo",
              },
              {
                icon: Landmark,
                title: "UKALA Member",
                tag: "Client Money Protection",
                text: "As a member of the UK Association of Letting Agents (UKALA), the rent and client money we hold is safeguarded by an approved Client Money Protection scheme \u2014 so your funds are fully protected.",
                badge: "Membership No. 18882",
                testid: "safe-hands-cmp",
              },
              {
                icon: Lock,
                title: "Deposit Protection Service",
                tag: "Tenancy Deposits",
                text: "All tenant deposits are registered with the government-backed DPS and handled in line with the law, protecting both landlord and tenant.",
                badge: "Membership No. 1125324",
                testid: "safe-hands-dps",
              },
              {
                icon: Users,
                title: "NRLA Member",
                tag: "Professional Membership",
                text: "We're members of the National Residential Landlords Association, keeping our practice fully up to date with the latest lettings legislation and best practice.",
                badge: "Membership No. 3014329",
                testid: "safe-hands-nrla",
              },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} data-testid={c.testid}
                  className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-[#d8b074]/40 hover:bg-white/[0.07]">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#d8b074]/15 text-[#d8b074] transition-colors group-hover:bg-[#d8b074]/25">
                    <Icon size={24} strokeWidth={1.8} />
                  </span>
                  <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#35a4a4]">{c.tag}</p>
                  <p className="mt-1 font-display text-xl font-600 text-white">{c.title}</p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-white/70">{c.text}</p>
                  {c.badge && (
                    <p className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-[#d8b074]/30 bg-[#d8b074]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#d8b074]">
                      <BadgeCheck size={13} /> {c.badge}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HMO licensing CTA card */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-[1100px] px-5">
          <div className="flex flex-col items-center gap-6 rounded-2xl border border-[#eaa32a]/40 bg-[#fff8ec] p-8 text-center md:flex-row md:text-left lg:p-10">
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#b9822a]">Compliance</p>
              <h3 className="mt-2 font-display text-2xl font-600 text-[#16323f]">New HMO &amp; selective licensing rules — is your property affected?</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#5b5450]">Most Hackney rentals now need a licence. Read our plain-English guide and use the quick checker to find out.</p>
            </div>
            <Link to="/landlords/hmo-licensing-hackney" className="group flex shrink-0 items-center gap-2 rounded-full bg-[#ec008c] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Read the guide <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f3ede3] py-16 lg:py-24">
        <div className="mx-auto max-w-[820px] px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              Landlord FAQs
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <p className="mx-auto mt-4 max-w-2xl text-[15px] text-[#5b5450]">
              Letting in Hackney? Read our{" "}
              <Link to="/landlords/hmo-licensing-hackney" className="font-semibold text-[#ec008c] underline">
                HMO &amp; Selective Licensing guide
              </Link>{" "}
              to check whether your property needs a licence.
            </p>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-4">
            {LANDLORD_FAQ.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="overflow-hidden rounded-2xl border border-[#e5ddd0] bg-white px-5"
              >
                <AccordionTrigger className="py-5 text-left font-display text-lg font-500 text-[#16323f] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-[15px] leading-relaxed text-[#5b5450]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#16323f] py-16">
        <div className="mx-auto flex max-w-[1000px] flex-col items-center px-5 text-center">
          <div className="mb-3 flex gap-1">
            {Array.from({ length: 5 }).map((_, s) => (<Star key={s} size={18} className="text-[#eaa32a]" fill="#eaa32a" />))}
          </div>
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-white lg:text-4xl">
            Ready to let with New Space?
          </h2>
          <p className="mt-3 text-white/75">Book a free, no-obligation valuation with our team.</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button onClick={openValuation}
              className="group flex items-center justify-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Request a free valuation <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="mt-6 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
            <PhoneCall size={16} className="text-[#d8b074]" /> Or call us on {CONTACT.phone}
          </a>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function Landlords() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
